/**
 * Hosted pricing / billing-rates helpers.
 *
 * Call sites stay root-url-less: the environment API root is injected here via getApiUrl().
 */
import {
  applyBillingRates,
  billingPeriodStartFromIso,
  billingPeriodStartUtc,
  billingRatesReady,
  billableWorkCost,
  computeCpuCharge,
  computeHostedDataTransferCharge,
  computeHostedForkDataTransferCharge,
  computeHostedForkQueryCharge,
  computeHostedQueryCharge,
  computeHostedUsageCharges,
  computeMemCharge,
  computeQueryBillingUnits,
  computeForkQueryBillingUnits,
  computeSeedSnapshotStorageCharge,
  cpuCostFromMillicoreHours,
  cpuUnitsToMillicoreHours,
  dailyHostedQueryCostUsd,
  dailyHostedForkQueryCostUsd,
  dailyQueryBillingUnits,
  dollarsPerBillingUnit,
  estimateMonthlyHostedCharges,
  formatConsumptionAmount,
  formatCpuCostFormula,
  formatCpuRate,
  formatDataTransferBytes,
  formatMemCostFormula,
  formatMemRate,
  formatQueryCostFormula,
  formatQueryStartingRate,
  formatRatePerUnit,
  formatSeedSnapshotStorageRate,
  formatStorageCostFormula,
  formatStorageRate,
  formatTransferCostFormula,
  formatTransferStartingRate,
  formatUnitRate,
  formatUsd,
  formatUsdFlexible,
  formatUsdPrecise,
  getBillingMeter,
  getBillingRates,
  incrementalHostedQueryCharge,
  incrementalHostedForkQueryCharge,
  incrementalQueryBillingUnits,
  isValidBillingRates,
  memCostFromMibHours,
  memUnitsToMibHours,
  queryCountToBillingUnits,
  requireBillingRates,
  storageCostFromMibHours,
  fetchBillingRates as fetchBillingRatesWithBase,
  fetchBillingRatesData as fetchBillingRatesDataWithBase,
  type BillingRates,
  type BillingMeter,
  type BillingUnit,
  type QueryTier,
  type TransferTier,
} from '@veilstream/billing-rates'
import { getApiUrl } from '../config'

export type { BillingRates, BillingMeter, BillingUnit, QueryTier, TransferTier }

export {
  applyBillingRates,
  billingPeriodStartFromIso,
  billingPeriodStartUtc,
  billingRatesReady,
  billableWorkCost,
  computeCpuCharge,
  computeHostedDataTransferCharge,
  computeHostedForkDataTransferCharge,
  computeHostedForkQueryCharge,
  computeHostedQueryCharge,
  computeHostedUsageCharges,
  computeMemCharge,
  computeQueryBillingUnits,
  computeForkQueryBillingUnits,
  computeSeedSnapshotStorageCharge,
  cpuCostFromMillicoreHours,
  cpuUnitsToMillicoreHours,
  dailyHostedQueryCostUsd,
  dailyHostedForkQueryCostUsd,
  dailyQueryBillingUnits,
  dollarsPerBillingUnit,
  estimateMonthlyHostedCharges,
  formatConsumptionAmount,
  formatCpuCostFormula,
  formatCpuRate,
  formatDataTransferBytes,
  formatMemCostFormula,
  formatMemRate,
  formatQueryCostFormula,
  formatQueryStartingRate,
  formatRatePerUnit,
  formatSeedSnapshotStorageRate,
  formatStorageCostFormula,
  formatStorageRate,
  formatTransferCostFormula,
  formatTransferStartingRate,
  formatUnitRate,
  formatUsd,
  formatUsdFlexible,
  formatUsdPrecise,
  getBillingMeter,
  getBillingRates,
  incrementalHostedQueryCharge,
  incrementalHostedForkQueryCharge,
  incrementalQueryBillingUnits,
  isValidBillingRates,
  memCostFromMibHours,
  memUnitsToMibHours,
  queryCountToBillingUnits,
  requireBillingRates,
  storageCostFromMibHours,
}

/** Fetch canonical rates for the active API environment (root injected once). */
export async function fetchBillingRates(year?: number, month?: number): Promise<boolean> {
  return fetchBillingRatesWithBase(getApiUrl(), year, month)
}

export async function fetchBillingRatesData(
  year?: number,
  month?: number,
): Promise<BillingRates | null> {
  return fetchBillingRatesDataWithBase(getApiUrl(), year, month)
}
